import { NavLink } from "react-router-dom";
import type { CSSProperties } from "react";
import { useIsMobile } from "../../lib/useIsMobile";

export type RecipeStudioTab =
  | "cocktails"
  | "preps"
  | "ingredients"
  | "mapping"
  | "flights"
  | "shelfMap"
  | "tastingMat";

interface Props {
  activeTab: RecipeStudioTab;
  badgeCounts?: {
    cocktails?: number;
    preps?: number;
    ingredients?: number;
    mapping?: number;
    flights?: number;
    shelfMap?: number;
    tastingMat?: number;
  };
}

const TABS: {
  id: RecipeStudioTab;
  to: string;
  label: string;
  shortLabel: string;
  countKey?: keyof NonNullable<Props["badgeCounts"]>;
}[] = [
  { id: "cocktails", to: "/admin/recipes", label: "🍸 Cocktail Specs", shortLabel: "🍸 Specs", countKey: "cocktails" },
  { id: "preps", to: "/admin/prep-recipes", label: "🧪 House Preps & Syrups", shortLabel: "🧪 Preps", countKey: "preps" },
  { id: "ingredients", to: "/admin/ingredients", label: "🌿 Raw Ingredients", shortLabel: "🌿 Ingredients", countKey: "ingredients" },
  { id: "mapping", to: "/admin/cocktail-mapping", label: "🔗 POS Pour Mapping", shortLabel: "🔗 POS Mapping", countKey: "mapping" },
  { id: "flights", to: "/admin/flights", label: "🥃 Flights Curation", shortLabel: "🥃 Flights", countKey: "flights" },
  { id: "shelfMap", to: "/admin/shelf-map", label: "🗺️ Bottle Shelf Map", shortLabel: "🗺️ Shelf Map", countKey: "shelfMap" },
  { id: "tastingMat", to: "/admin/tasting-mat", label: "📜 Tasting Mat Creator", shortLabel: "📜 Tasting Mats", countKey: "tastingMat" },
];

export default function RecipeStudioTabs({ activeTab, badgeCounts }: Props) {
  const isMobile = useIsMobile(768);

  return (
    <div style={{ ...styles.container, marginBottom: isMobile ? 10 : 20, paddingBottom: isMobile ? 8 : 12 }}>
      {!isMobile && (
        <div style={styles.headerRow}>
          <div>
            <div style={styles.badge}>Floor &amp; Cocktails Studio</div>
            <h1 style={styles.title}>Floor, Cocktails &amp; Service Studio</h1>
            <p style={styles.subtitle}>
              Manage cocktail builds, batch syrups, raw ingredients, Square POS mappings, flights curation, and printable tasting mats.
            </p>
          </div>
        </div>
      )}

      {/* Single-row horizontal swipe rail on mobile, flex row on desktop */}
      <nav
        style={{
          ...styles.tabNav,
          flexWrap: isMobile ? "nowrap" : "wrap",
          overflowX: isMobile ? "auto" : "visible",
          WebkitOverflowScrolling: "touch",
          gap: isMobile ? 6 : 8,
          paddingBottom: isMobile ? 4 : 0,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        aria-label="Floor and Cocktails Studio Tabs"
      >
        {TABS.map((tab) => {
          const isActive = tab.id === activeTab;
          const count = tab.countKey ? badgeCounts?.[tab.countKey] : undefined;
          return (
            <NavLink
              key={tab.id}
              to={tab.to}
              style={{
                ...styles.tabLink,
                padding: isMobile ? "6px 12px" : "8px 14px",
                fontSize: isMobile ? 12 : 13,
                flex: "0 0 auto",
                borderRadius: isMobile ? 20 : 8,
                ...(isActive ? styles.tabLinkActive : {}),
              }}
            >
              <span>{isMobile ? tab.shortLabel : tab.label}</span>
              {typeof count === "number" && (
                <span style={isActive ? styles.countActive : styles.countInactive}>
                  {count}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    borderBottom: "1px solid var(--border)",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 14,
    flexWrap: "wrap",
    gap: 12,
  },
  headerRowMobile: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  mobileHero: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  badge: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "var(--gold)",
    background: "rgba(249, 175, 54, 0.12)",
    padding: "3px 8px",
    borderRadius: 4,
  },
  title: {
    margin: "6px 0 0 0",
    fontSize: 22,
    fontWeight: 700,
    color: "var(--text)",
    letterSpacing: "-0.01em",
  },
  titleMobile: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    color: "var(--text)",
    letterSpacing: "-0.01em",
  },
  mobileCountHint: {
    fontSize: 11,
    color: "var(--muted)",
    fontWeight: 600,
  },
  subtitle: {
    margin: "4px 0 0 0",
    fontSize: 13,
    color: "var(--muted)",
  },
  tabNav: {
    display: "flex",
    width: "100%",
  },
  tabLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    borderRadius: 8,
    fontWeight: 600,
    color: "var(--muted)",
    textDecoration: "none",
    background: "rgba(255, 255, 255, 0.02)",
    border: "1px solid var(--border)",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
    minHeight: 36,
  },
  tabLinkActive: {
    color: "#0D0B08",
    background: "var(--gold)",
    borderColor: "var(--gold)",
    fontWeight: 700,
  },
  countActive: {
    fontSize: 11,
    background: "rgba(0,0,0,0.18)",
    color: "#0D0B08",
    padding: "1px 6px",
    borderRadius: 10,
    fontWeight: 800,
  },
  countInactive: {
    fontSize: 11,
    background: "rgba(255,255,255,0.06)",
    color: "var(--muted)",
    padding: "1px 6px",
    borderRadius: 10,
    fontWeight: 600,
  },
};
